import { OrderUnitOfMeasure, TicketState } from '@treadinc/horizon-api-spec';
import dayjs from 'dayjs';
import { isNil } from 'lodash-es';

import { Job } from '~hooks/useJob/models';
import { Load } from '~hooks/useLoad/models';
import { Ticket } from '~hooks/useTickets/models';
import { Nullable } from '~types/Nullable';

export const canEditTicket = (state: Nullable<TicketState>) => {
  return state === TicketState.PENDING || state === TicketState.NEW;
};

export const getTicketQuantityAndUom = (ticket?: Ticket, load?: Load, job?: Job) => {
  const ticketQuantity = ticket?.quantity || ticket?.ocrNetQuantityTons || null;

  if (!isNil(ticketQuantity) && ticket?.unitOfMeasure?.name) {
    return {
      quantity: Number(Number(ticketQuantity).toFixed(2)),
      uom: ticket.unitOfMeasure.name as OrderUnitOfMeasure,
    };
  }

  let defaultQuantity: number | null = null;

  if (
    job?.equipmentTypeGrossCapacity &&
    job?.quantity &&
    job.equipmentTypeGrossCapacityUnitOfMeasure?.id !== OrderUnitOfMeasure.LOAD &&
    job.equipmentTypeGrossCapacityUnitOfMeasure?.id === job?.unitOfMeasure?.id
  ) {
    // handle partial load case
    defaultQuantity = Math.min(
      Number(Number(job.equipmentTypeGrossCapacity).toFixed(2)),
      Number(Number(job.quantity).toFixed(2)),
    );
  } else if (
    job?.equipmentTypeGrossCapacity &&
    job.equipmentTypeGrossCapacityUnitOfMeasure?.id !== OrderUnitOfMeasure.LOAD
  ) {
    // otherwise, use equipment capacity
    defaultQuantity = Number(Number(job.equipmentTypeGrossCapacity).toFixed(2));
  } else if (!isNil(job?.quantity)) {
    defaultQuantity = Number(Number(job.quantity).toFixed(2));
  }

  let defaultUom: OrderUnitOfMeasure | null = null;

  // use job unit of measure if it isn't Load
  if (job?.unitOfMeasure?.id && job.unitOfMeasure.id !== OrderUnitOfMeasure.LOAD) {
    defaultUom = job.unitOfMeasure.name as OrderUnitOfMeasure;
  }

  // use equipment capacity unit of measure if it isn't Load
  else if (
    job?.equipmentTypeGrossCapacityUnitOfMeasure?.id &&
    job.equipmentTypeGrossCapacityUnitOfMeasure.id !== OrderUnitOfMeasure.LOAD
  ) {
    defaultUom = job.equipmentTypeGrossCapacityUnitOfMeasure.name as OrderUnitOfMeasure;
  }

  if (defaultQuantity !== null && defaultUom) {
    return {
      quantity: defaultQuantity,
      uom: defaultUom,
    };
  }

  return null;
};

export const getTicketMaterial = (ticket?: Ticket, load?: Load, job?: Job) => {
  return ticket?.material ?? load?.material ?? job?.material ?? null;
};

export const getTicketDatetime = (ticket?: Ticket, defaultToNow = false) => {
  const { serviceDate, ocrServiceDate, createdAt } = ticket ?? {};

  try {
    const targetDatetime = serviceDate || ocrServiceDate || createdAt;
    const targetDatetimeWithTz = targetDatetime
      ? dayjs.tz(dayjs(targetDatetime))
      : defaultToNow
        ? dayjs.tz(dayjs())
        : null;

    return targetDatetimeWithTz?.isValid() ? targetDatetimeWithTz : null;
  } catch {
    return null;
  }
};

export const getTicketNumber = (ticket?: Ticket) => {
  return ticket?.ticketNumber || ticket?.ocrTicketNumber || null;
};
